import { openDB, type IDBPDatabase } from 'idb';
import type {
  Box,
  BoxDraft,
  CustomCategory,
  CustomRoom,
  Item,
  ItemDraft,
  ItemImage,
  MoveProfile,
  MoveProfileDraft,
} from '@easymoving/types';
import { compressImage } from '../images/compress';
import type { Backend, Unsubscribe } from './types';

const DB_NAME = 'easymoving-local';
const DB_VERSION = 2;
const PROFILE_KEY = 'profile';

function uuid(): string {
  return crypto.randomUUID();
}

/**
 * On-device backend for guests (no account). Everything lives in IndexedDB on
 * this device — items/boxes/custom taxonomy as records, photos as Blobs that
 * are surfaced as object-URLs on read. No cloud, no server thumbnails. The
 * same data shape as the cloud backend, so migration (Phase 3) is a copy.
 */
export class LocalBackend implements Backend {
  readonly mode = 'local' as const;
  private dbp: Promise<IDBPDatabase>;
  private urls = new Map<string, string>(); // blobKey -> object URL
  private itemSubs = new Set<(i: Item[]) => void>();
  private boxSubs = new Set<(b: Box[]) => void>();
  private roomSubs = new Set<(r: CustomRoom[]) => void>();
  private catSubs = new Set<(c: CustomCategory[]) => void>();
  private profileSubs = new Set<(p: MoveProfile | null) => void>();

  constructor() {
    this.dbp = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create each store only if missing, so upgrades from older versions
        // keep existing data.
        for (const s of ['items', 'boxes', 'rooms', 'categories'] as const) {
          if (!db.objectStoreNames.contains(s)) db.createObjectStore(s, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('blobs')) db.createObjectStore('blobs');
        if (!db.objectStoreNames.contains('profile')) db.createObjectStore('profile');
      },
    });
  }

  private blobUrl(key: string, blob: Blob): string {
    let u = this.urls.get(key);
    if (!u) {
      u = URL.createObjectURL(blob);
      this.urls.set(key, u);
    }
    return u;
  }
  private async removeBlob(key: string | undefined) {
    if (!key) return;
    await (await this.dbp).delete('blobs', key);
    const u = this.urls.get(key);
    if (u) {
      URL.revokeObjectURL(u);
      this.urls.delete(key);
    }
  }

  private async hydrateItem(rec: Item): Promise<Item> {
    const db = await this.dbp;
    const imgs = await Promise.all(
      (rec.images ?? []).map(async (img) => {
        const blob = img.storagePath ? await db.get('blobs', img.storagePath) : undefined;
        if (!blob) return img;
        const url = this.blobUrl(img.storagePath, blob);
        return { ...img, url, thumbUrl: url };
      }),
    );
    const primary = imgs.find((i) => i.isPrimary) ?? imgs[0];
    return {
      ...rec,
      images: imgs,
      primaryImageUrl: primary?.url,
      primaryThumbUrl: primary?.thumbUrl ?? primary?.url,
    };
  }
  private async listItems(): Promise<Item[]> {
    const recs = (await (await this.dbp).getAll('items')) as Item[];
    const out = await Promise.all(recs.map((r) => this.hydrateItem(r)));
    return out.sort((a, b) => b.createdAt - a.createdAt);
  }
  private async notifyItems() {
    const items = await this.listItems();
    this.itemSubs.forEach((cb) => cb(items));
  }

  subscribeItems(cb: (i: Item[]) => void): Unsubscribe {
    this.itemSubs.add(cb);
    void this.listItems().then(cb);
    return () => this.itemSubs.delete(cb);
  }
  async getItem(id: string): Promise<Item | null> {
    const rec = (await (await this.dbp).get('items', id)) as Item | undefined;
    return rec ? this.hydrateItem(rec) : null;
  }
  async createItem(draft: ItemDraft): Promise<string> {
    const id = uuid();
    const now = Date.now();
    const rec: Item = {
      ...draft,
      id,
      images: [],
      destination: draft.destination ?? 'pack',
      progressStatus: draft.progressStatus ?? 'not_started',
      quantity: draft.quantity ?? 1,
      fragile: draft.fragile ?? false,
      createdAt: now,
      updatedAt: now,
    };
    await (await this.dbp).put('items', rec);
    void this.notifyItems();
    return id;
  }
  async updateItem(id: string, patch: Partial<Omit<Item, 'id' | 'createdAt'>>): Promise<void> {
    const db = await this.dbp;
    const rec = (await db.get('items', id)) as Item | undefined;
    if (!rec) return;
    await db.put('items', { ...rec, ...patch, updatedAt: Date.now() });
    void this.notifyItems();
  }
  async deleteItem(id: string): Promise<void> {
    const db = await this.dbp;
    const rec = (await db.get('items', id)) as Item | undefined;
    await db.delete('items', id);
    for (const img of rec?.images ?? []) await this.removeBlob(img.storagePath);
    void this.notifyItems();
  }
  async duplicateItem(id: string): Promise<string | null> {
    const db = await this.dbp;
    const rec = (await db.get('items', id)) as Item | undefined;
    if (!rec) return null;
    const newId = uuid();
    const now = Date.now();
    const images: ItemImage[] = [];
    for (const img of rec.images ?? []) {
      const blob = await db.get('blobs', img.storagePath);
      if (blob) {
        const key = `items/${newId}/original`;
        await db.put('blobs', blob, key);
        images.push({ ...img, storagePath: key });
      }
    }
    await db.put('items', { ...rec, id: newId, images, createdAt: now, updatedAt: now });
    void this.notifyItems();
    return newId;
  }

  async uploadPrimaryImage(itemId: string, file: File): Promise<string> {
    const db = await this.dbp;
    const compressed = await compressImage(file);
    const key = `items/${itemId}/original`;
    await this.removeBlob(key);
    await db.put('blobs', compressed, key);
    const rec = (await db.get('items', itemId)) as Item | undefined;
    if (rec) {
      const image: ItemImage = { storagePath: key, isPrimary: true, createdAt: Date.now() };
      await db.put('items', { ...rec, images: [image], updatedAt: Date.now() });
    }
    void this.notifyItems();
    return this.blobUrl(key, compressed);
  }
  async clearPrimaryImage(itemId: string): Promise<void> {
    const db = await this.dbp;
    const rec = (await db.get('items', itemId)) as Item | undefined;
    if (!rec) return;
    for (const img of rec.images ?? []) await this.removeBlob(img.storagePath);
    await db.put('items', { ...rec, images: [], updatedAt: Date.now() });
    void this.notifyItems();
  }
  async deleteItemImages(itemId: string): Promise<void> {
    await this.removeBlob(`items/${itemId}/original`);
  }
  async addImage(itemId: string, file: File): Promise<void> {
    const db = await this.dbp;
    const rec = (await db.get('items', itemId)) as Item | undefined;
    if (!rec) return;
    const images = rec.images ?? [];
    const first = images.length === 0;
    const key = first ? `items/${itemId}/original` : `items/${itemId}/img-${Date.now()}-${images.length}`;
    const compressed = await compressImage(file);
    await db.put('blobs', compressed, key);
    const img: ItemImage = { storagePath: key, isPrimary: first, createdAt: Date.now() };
    await db.put('items', { ...rec, images: [...images, img], updatedAt: Date.now() });
    void this.notifyItems();
  }
  async removeImage(itemId: string, storagePath: string): Promise<void> {
    const db = await this.dbp;
    const rec = (await db.get('items', itemId)) as Item | undefined;
    if (!rec) return;
    await this.removeBlob(storagePath);
    const target = (rec.images ?? []).find((i) => i.storagePath === storagePath);
    const remaining = (rec.images ?? []).filter((i) => i.storagePath !== storagePath);
    if ((target?.isPrimary || remaining.every((i) => !i.isPrimary)) && remaining.length > 0) {
      remaining[0]!.isPrimary = true;
    }
    await db.put('items', { ...rec, images: remaining, updatedAt: Date.now() });
    void this.notifyItems();
  }
  async setPrimaryImage(itemId: string, storagePath: string): Promise<void> {
    const db = await this.dbp;
    const rec = (await db.get('items', itemId)) as Item | undefined;
    if (!rec) return;
    const images = (rec.images ?? []).map((i) => ({ ...i, isPrimary: i.storagePath === storagePath }));
    await db.put('items', { ...rec, images, updatedAt: Date.now() });
    void this.notifyItems();
  }

  // boxes
  private async listBoxes(): Promise<Box[]> {
    const recs = (await (await this.dbp).getAll('boxes')) as Box[];
    return recs.sort((a, b) => b.createdAt - a.createdAt);
  }
  private async notifyBoxes() {
    const boxes = await this.listBoxes();
    this.boxSubs.forEach((cb) => cb(boxes));
  }
  subscribeBoxes(cb: (b: Box[]) => void): Unsubscribe {
    this.boxSubs.add(cb);
    void this.listBoxes().then(cb);
    return () => this.boxSubs.delete(cb);
  }
  async getBox(id: string): Promise<Box | null> {
    return ((await (await this.dbp).get('boxes', id)) as Box | undefined) ?? null;
  }
  async createBox(draft: BoxDraft): Promise<string> {
    const id = uuid();
    const now = Date.now();
    await (await this.dbp).put('boxes', { ...draft, id, createdAt: now, updatedAt: now });
    void this.notifyBoxes();
    return id;
  }
  async updateBox(id: string, patch: Partial<Omit<Box, 'id' | 'createdAt'>>): Promise<void> {
    const db = await this.dbp;
    const rec = (await db.get('boxes', id)) as Box | undefined;
    if (!rec) return;
    await db.put('boxes', { ...rec, ...patch, updatedAt: Date.now() });
    void this.notifyBoxes();
  }
  async deleteBox(id: string): Promise<void> {
    await (await this.dbp).delete('boxes', id);
    void this.notifyBoxes();
  }

  // taxonomy
  private async listRooms(): Promise<CustomRoom[]> {
    const r = (await (await this.dbp).getAll('rooms')) as CustomRoom[];
    return r.sort((a, b) => a.name.localeCompare(b.name));
  }
  private async listCategories(): Promise<CustomCategory[]> {
    const c = (await (await this.dbp).getAll('categories')) as CustomCategory[];
    return c.sort((a, b) => a.name.localeCompare(b.name));
  }
  private async notifyRooms() {
    const r = await this.listRooms();
    this.roomSubs.forEach((cb) => cb(r));
  }
  private async notifyCats() {
    const c = await this.listCategories();
    this.catSubs.forEach((cb) => cb(c));
  }
  subscribeCustomRooms(cb: (r: CustomRoom[]) => void): Unsubscribe {
    this.roomSubs.add(cb);
    void this.listRooms().then(cb);
    return () => this.roomSubs.delete(cb);
  }
  subscribeCustomCategories(cb: (c: CustomCategory[]) => void): Unsubscribe {
    this.catSubs.add(cb);
    void this.listCategories().then(cb);
    return () => this.catSubs.delete(cb);
  }
  async createCustomRoom(name: string): Promise<string> {
    const id = uuid();
    const now = Date.now();
    await (await this.dbp).put('rooms', { id, name, createdAt: now, updatedAt: now });
    void this.notifyRooms();
    return id;
  }
  async createCustomCategory(name: string): Promise<string> {
    const id = uuid();
    const now = Date.now();
    await (await this.dbp).put('categories', { id, name, createdAt: now, updatedAt: now });
    void this.notifyCats();
    return id;
  }
  private async cascade(field: 'room' | 'category', ids: string[], value: string | undefined) {
    const db = await this.dbp;
    for (const id of ids) {
      const rec = (await db.get('items', id)) as Item | undefined;
      if (rec) await db.put('items', { ...rec, [field]: value, updatedAt: Date.now() });
    }
    if (ids.length) void this.notifyItems();
  }
  async renameCustomRoom(id: string, newName: string, affectedItemIds: string[]): Promise<void> {
    const db = await this.dbp;
    const rec = (await db.get('rooms', id)) as CustomRoom | undefined;
    if (rec) await db.put('rooms', { ...rec, name: newName, updatedAt: Date.now() });
    await this.cascade('room', affectedItemIds, newName);
    void this.notifyRooms();
  }
  async deleteCustomRoom(id: string, affectedItemIds: string[]): Promise<void> {
    await this.cascade('room', affectedItemIds, undefined);
    await (await this.dbp).delete('rooms', id);
    void this.notifyRooms();
  }
  async renameCustomCategory(id: string, newName: string, affectedItemIds: string[]): Promise<void> {
    const db = await this.dbp;
    const rec = (await db.get('categories', id)) as CustomCategory | undefined;
    if (rec) await db.put('categories', { ...rec, name: newName, updatedAt: Date.now() });
    await this.cascade('category', affectedItemIds, newName);
    void this.notifyCats();
  }
  async deleteCustomCategory(id: string, affectedItemIds: string[]): Promise<void> {
    await this.cascade('category', affectedItemIds, undefined);
    await (await this.dbp).delete('categories', id);
    void this.notifyCats();
  }

  // move profile
  private async readProfile(): Promise<MoveProfile | null> {
    return ((await (await this.dbp).get('profile', PROFILE_KEY)) as MoveProfile | undefined) ?? null;
  }
  private async notifyProfile() {
    const p = await this.readProfile();
    this.profileSubs.forEach((cb) => cb(p));
  }
  getMoveProfile(): Promise<MoveProfile | null> {
    return this.readProfile();
  }
  subscribeMoveProfile(cb: (p: MoveProfile | null) => void): Unsubscribe {
    this.profileSubs.add(cb);
    void this.readProfile().then(cb);
    return () => this.profileSubs.delete(cb);
  }
  async saveMoveProfile(patch: MoveProfileDraft): Promise<void> {
    const db = await this.dbp;
    const cur = ((await db.get('profile', PROFILE_KEY)) as MoveProfile | undefined) ?? {};
    await db.put('profile', { ...cur, ...patch, updatedAt: Date.now() }, PROFILE_KEY);
    void this.notifyProfile();
  }

  // --- migration support (Phase 3) ---

  /** True if there's any guest data on this device. */
  async hasData(): Promise<boolean> {
    const db = await this.dbp;
    const counts = await Promise.all([
      db.count('items'),
      db.count('boxes'),
      db.count('rooms'),
      db.count('categories'),
    ]);
    return counts.some((n) => n > 0);
  }

  /** Raw records + photo blobs, for migrating to the cloud on sign-in. */
  async exportForMigration(): Promise<{
    items: Item[];
    boxes: Box[];
    rooms: CustomRoom[];
    categories: CustomCategory[];
    profile: MoveProfile | null;
    blobs: Map<string, Blob>;
  }> {
    const db = await this.dbp;
    const items = (await db.getAll('items')) as Item[];
    const boxes = (await db.getAll('boxes')) as Box[];
    const rooms = (await db.getAll('rooms')) as CustomRoom[];
    const categories = (await db.getAll('categories')) as CustomCategory[];
    const profile = await this.readProfile();
    const blobs = new Map<string, Blob>();
    for (const it of items) {
      for (const img of it.images ?? []) {
        const blob = (await db.get('blobs', img.storagePath)) as Blob | undefined;
        if (blob) blobs.set(img.storagePath, blob);
      }
    }
    return { items, boxes, rooms, categories, profile, blobs };
  }

  /** Wipe all local data (after a successful migration). */
  async clearAll(): Promise<void> {
    const db = await this.dbp;
    await Promise.all(
      (['items', 'boxes', 'rooms', 'categories', 'blobs', 'profile'] as const).map((s) =>
        db.clear(s),
      ),
    );
    for (const u of this.urls.values()) URL.revokeObjectURL(u);
    this.urls.clear();
    void this.notifyItems();
    void this.notifyBoxes();
    void this.notifyRooms();
    void this.notifyCats();
    void this.notifyProfile();
  }
}
